import { TrackingConfigPreset, TrackLib } from '@poseidon/tracklib';
import {
  CONFIG_ENVIRONMENT,
  CONFIG_TRACKING_ENABLED,
  CONFIG_TRACKING_SAMPLE_RATE,
  CONFIG_VERSION,
} from '../../config';
import { TrackingSystemParameters, TrackingSystemProperties } from '@poseidon/tracklib/lib/tracklib.systems';
import { getCacheBuster } from '../..';
import * as Logger from '../../common/tools/logger';
import { getChanceResult } from './util';

const trackers: { [key: string]: TrackLib } = {};

export interface TrackingSettings {
  softwareName: string;
  softwareVersion?: string;
  brand: string;
  touchpointName?: string;
  touchpointReference?: string;
  requestedPermission?: string;
}

let sendAllRequests = false;

export function initTracker(settings: TrackingSettings): TrackLib | null {
  /* istanbul ignore next if */
  if (!CONFIG_TRACKING_ENABLED) {
    return null;
  }

  if (trackers[settings.softwareName]) {
    return trackers[settings.softwareName];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (typeof (window as any).TrackLib !== 'function') {
    Logger.error('tracker', 'initTracker', 'TrackLib not found!');
    return null;
  }

  sendAllRequests = getChanceResult(CONFIG_TRACKING_SAMPLE_RATE || 1);

  /* eslint-disable @typescript-eslint/camelcase */
  const preset: TrackingConfigPreset = {
    lib: {
      properties: {
        lib: {
          // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
          // @ts-ignore
          stage: CONFIG_ENVIRONMENT ==='PRODUCTION' /* istanbul ignore next if */ ? 'live' : 'qa'
        }
      },
      parameter: {
        soft_name: settings.softwareName,
        soft_version: settings.softwareVersion || CONFIG_VERSION,
        brand: settings.brand === 'gmx' /* istanbul ignore next if */?  'gmxnet' : settings.brand,
        corr_process_id: getCacheBuster()
      }
    }
  };

  if (settings.requestedPermission) {
    preset.lib.parameter.cont_requested_perm = settings.requestedPermission;
  }
  /* istanbul ignore next else */
  if (settings.touchpointName) {
    preset.lib.parameter.cont_touchpoint_id = settings.touchpointName;
  }
  /* istanbul ignore next else */
  if (settings.touchpointReference) {
    let sanitizedUrl: string;
    try {
      const tpRefUrl = new URL(settings.touchpointReference);
      sanitizedUrl = `${tpRefUrl.protocol}//${tpRefUrl.host}${tpRefUrl.pathname}`;
      // eslint-disable-next-line no-empty
    } catch (ex) {}

    preset.lib.parameter.cont_touchpoint_ref = sanitizedUrl || settings.touchpointReference;
  }

  /* eslint-enable @typescript-eslint/camelcase */

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trackers[settings.softwareName] = new (window as any).TrackLib(preset);
  const samplingMsg = sendAllRequests ? 'Sampling is disabled, sending all requests.'
    : `Sampling is enabled with a rate of ${CONFIG_TRACKING_SAMPLE_RATE}`
  Logger.debug('tracker', 'initTracker', 'Tracklib is ready', samplingMsg);

  return trackers[settings.softwareName];
}

export function track(softwareName: string, parameters: TrackingSystemParameters, properties?: TrackingSystemProperties): TrackLib | null {
  /* istanbul ignore next if */
  if (!CONFIG_TRACKING_ENABLED) {
    return null;
  }

  if (!sendAllRequests && typeof parameters.sett_sample === 'number' && parameters.sett_sample !== 1) {
    return null;
  }

  /* istanbul ignore next if */
  if (!trackers[softwareName]) {
    Logger.warn('tracker', 'track', `Tracklib has not been initialized, call 'initTracker' first!`);
    return null;
  }

  /* istanbul ignore next else */
  if (!parameters.trackingtype) {
    parameters.trackingtype = 'ev';
  }

  return trackers[softwareName].track(parameters, properties);
}
